'use strict';

define('vb/extensions/dynamic/helpers/dynamicUiHelper',[], () => {
  /** @typedef {import('vb/private/stateManagement/container').default} Container */

  /**
   * Symbol used to access the delegate helper
   * @type {symbol}
   */
  const delegate = Symbol('delegate-helper');

  /**
   * This class represents the public contract between Dynamic UI and VB for loading
   * layout resources.
   * @category Helper
   */
  class DynamicUiHelper {
    /**
     * Creates a new instance of DynamicUiHelper.
     *
     * @param {object} delegateHelper - The delegate helper object that provides the necessary functionality.
     */
    constructor(delegateHelper) {
      this[delegate] = delegateHelper;
    }

    /**
     * Returns an object that contains all information required by services layer to locate a service.
     *
     * @returns {Object}
     */
    get vbContext() {
      return this[delegate].vbContext;
    }

    /**
     * Returns true if the layout is runnable on the server, e.g., a BOSS layout.
     *
     * @returns {boolean}
     */
    get isRunnableOnServer() {
      return this[delegate].isRunnableOnServer;
    }

    /**
     * Expose the underlying VB container so dynamic ui can detect if a metadata descriptor has been
     * disposed.
     *
     * Note: This is a temporary workaround that will be removed in 2410. The real solution is for VB
     * to call dispose on the metadata provider.
     *
     * @returns {Container}
     */
    get container() {
      return this[delegate].container;
    }

    /**
     * Retrieves resources associated with global field templates.
     *
     * @async
     * @returns {Promise<Array>} A promise resolving to an array of global resource objects.
     */
    getGlobalResources() {
      return this[delegate].getGlobalResources();
    }

    /**
     * Retrieves shared global field templates from base extensions.
     *
     * @async
     * @returns {Promise<Array>} An array of global field template resources from all base extensions.
     */
    getSharedGlobalResources() {
      return this[delegate].getSharedGlobalResources();
    }

    /**
     * Retrieves resources associated with the current layout.
     *
     * @async
     * @returns {Promise<Array>} A promise resolving to an array of layout resource objects.
     */
    getLayoutResources() {
      return this[delegate].getLayoutResources();
    }

    /**
     * Retrieves the metadata (OpenAPI 3 or data-description.json) along with a model, if available.
     *
     * @async
     * @returns {Promise<Object>} A promise resolving to an object containing the metadata and model.
     */
    getMetadata() {
      return this[delegate].getMetadata();
    }

    /**
     * Retrieves external metadata (OpenAPI 3 or data-description.json) along with response headers.
     *
     * @async
     * @param {string} path - The URL or path of the Swagger file to retrieve metadata from.
     * @returns {Promise<{data: *, headers: *}>} A promise resolving to an object containing the metadata and
     *                                           response headers.
     */
    getExternalMetadata(path) {
      return this[delegate].getExternalMetadata(path);
    }

    /**
     * This method returns a string representing a unique URL used for caching metadata.
     *
     * @returns {string} A unique URL as a string.
     */
    toUrl() {
      return this[delegate].toUrl();
    }

    /**
     * Returns a stable cache key for processed metadata.
     *
     * This key identifies the resolved service definition, not the helper instance.
     * It is stable across fragment instances and should be used by dyn UI to cache
     * processed metadata.
     *
     * @returns {Promise<string|null>} A promise resolving to the canonical cache key,
     *                                or null if the service cannot be resolved.
     */
    getKey() {
      return this[delegate].getKey();
    }

    /**
     * Loads a module defined outside of the layout, such as a transform function.
     *
     * If the module is defined in a V2 extension, its path will be adjusted accordingly before loading.
     *
     * @param {string} path - The path to the module to load.
     * @returns {Promise} A promise that resolves when the module has been loaded.
     */
    getExternalModuleResource(path) {
      return this[delegate].getExternalModuleResource(path);
    }

    /**
     * Creates a new instance of DynamicUiHelper for the specified options.
     *
     * @param {object} options - Options for creating the new helper instance.
     * @returns {Promise<DynamicUiHelper>} A promise resolving to a new instance of DynamicUiHelper.
     */
    getHelper(options) {
      // wrap helper in DynamicUiHelper so we don't expose internal implementation
      return this[delegate].getHelper(options)
        .then((helper) => new DynamicUiHelper(helper));
    }
  }

  return DynamicUiHelper;
});

